import { createContext, useState, useEffect, useContext } from 'react'

import { AuthContext } from '../../../context/AuthContext'
import { SessionContext } from '../../../context/SessionContext'

import { checkConsent, submitConsent } from '../../../service/consentService'

export const ConsentContext = createContext()

// GET /consent/check answers { hasConsented, consentedAt }, and for a student who declined that
// is { false, null } - the same as for one who never answered. So each answer the server saved
// is also remembered in this browser, and a reload does not ask a decliner again. Storage can be
// missing (private windows): the modal then asks again, as it always did.
const answerKey = (userId, sessionId) => `consent_answer_${sessionId}_${userId}`

const readSavedAnswer = (userId, sessionId) => {
  try {
    const value = localStorage.getItem(answerKey(userId, sessionId))
    return value === 'agreed' || value === 'declined' ? value : null
  } catch (error) {
    return null
  }
}

const rememberAnswer = (userId, sessionId, agreed) => {
  try {
    localStorage.setItem(answerKey(userId, sessionId), agreed ? 'agreed' : 'declined')
  } catch (error) {
    // The server still has it.
  }
}

export const ConsentProvider = ({ children }) => {
  const { currentUser } = useContext(AuthContext)
  const { session } = useContext(SessionContext)

  // hasAnswered closes the modal and lets the flow after it go on, whether the student agreed
  // or declined. hasConsented is only whether they agreed, which is all recording may go on.
  // Both null while loading.
  const [hasAnswered, setHasAnswered] = useState(null)
  const [hasConsented, setHasConsented] = useState(null)
  const [isProcessing, setIsProcessing] = useState(false)

  // Check consent status on mount
  useEffect(() => {
    const fetchConsentStatus = async () => {
      if (!currentUser?.id || !session?.id) return
      const saved = readSavedAnswer(currentUser.id, session.id)
      try {
        const result = await checkConsent(currentUser.id, session.id)
        // The server's word on agreeing wins over this browser's: a withdrawal there stops
        // recording here.
        const agreed = result?.hasConsented === true
        setHasConsented(agreed)
        setHasAnswered(agreed || saved !== null)
      } catch (error) {
        console.error('Error checking consent:', error)
        setHasConsented(saved === 'agreed')
        setHasAnswered(saved !== null)
      }
    }
    fetchConsentStatus()
  }, [currentUser?.id, session?.id])

  const handleConsent = async (didConsent) => {
    setIsProcessing(true)
    try {
      await submitConsent(currentUser.id, session.id, didConsent)
      rememberAnswer(currentUser.id, session.id, didConsent)
      setHasConsented(didConsent === true)
    } catch (error) {
      console.error('Failed to submit consent:', error)
      // The modal still closes, so the student is not stuck, but nothing is recorded on an
      // answer the server does not have; the next page load asks again.
      setHasConsented(false)
    } finally {
      setHasAnswered(true)
      setIsProcessing(false)
    }
  }

  return (
    <ConsentContext.Provider value={{
      hasAnswered,
      hasConsented,
      isProcessing,
      handleConsent,
    }}>
      {children}
    </ConsentContext.Provider>
  )
}
